import { BalanceDynamicsBucket, SpeechSegment } from '../../../services/api.service';
import {
  SPEAKER_COLORS,
  formatSpeakerFullName,
  formatSpeakerShortName,
  getSpeakerName,
} from './audio-segments';
import { resolveSpeakerColor } from './meeting-analytics';

/** Задача F1: разговорная/голосовая аналитика (filler_stats, conversation_metrics,
 *  emotion_analysis, prosody_analysis) — все посчитаны на бэкенде по `speaker_label`
 *  (сырой диаризационный ярлык вроде "SPEAKER_00"), а не по отображаемому имени спикера.
 *  Этот файл сопоставляет сырые ярлыки с уже посчитанным на фронте отображаемым именем/цветом
 *  спикера (speakerTimelines), не трогая существующие resolveSpeaker*-хелперы (они рассчитаны
 *  на display-имена, напр. из insights). */

export type SpeakerLabelMeta = {
  label: string;
  displayName: string;
  shortName: string;
  color: string;
};

export type SpeakerLabelIndex = Record<string, SpeakerLabelMeta>;

const UNRESOLVED_COLOR = '#a3a3a3';

/** Индекс "сырой speaker_label" → отображаемое имя/цвет, построенный по сегментам встречи
 *  (первое появление ярлыка) и уже посчитанным цветам спикеров (speakerTimelines). */
export function buildSpeakerLabelIndex(
  segments: SpeechSegment[],
  speakerTimelines: { name: string; color: string }[],
): SpeakerLabelIndex {
  const index: SpeakerLabelIndex = {};
  let fallbackIdx = 0;
  segments.forEach(seg => {
    const label = seg.speaker_label;
    if (!label || index[label]) return;
    const rawName = getSpeakerName(seg);
    const resolvedColor = resolveSpeakerColor(rawName, speakerTimelines);
    const color = resolvedColor === UNRESOLVED_COLOR
      ? SPEAKER_COLORS[fallbackIdx++ % SPEAKER_COLORS.length]
      : resolvedColor;
    index[label] = {
      label,
      displayName: formatSpeakerFullName(rawName),
      shortName: formatSpeakerShortName(rawName),
      color,
    };
  });
  return index;
}

/** Метаданные по сырому ярлыку с безопасным фолбэком (ярлык остаётся видимым, если
 *  сегментов с этим спикером почему-то не нашлось — не должно случаться, но не должно и упасть). */
export function resolveLabelMeta(
  label: string,
  index: SpeakerLabelIndex,
  fallbackIdx = 0,
): SpeakerLabelMeta {
  return index[label] || {
    label,
    displayName: label,
    shortName: label,
    color: SPEAKER_COLORS[fallbackIdx % SPEAKER_COLORS.length],
  };
}

/** Все сырые ярлыки, встречающиеся в данном наборе записей — сохраняет порядок первого
 *  появления в speakerLabelIndex (совпадает с хронологией сегментов). */
export function orderLabels(labels: Iterable<string>, index: SpeakerLabelIndex): string[] {
  const known = Object.keys(index);
  const set = new Set(labels);
  const ordered = known.filter(l => set.has(l));
  const rest = Array.from(set).filter(l => !known.includes(l)).sort();
  return ordered.concat(rest);
}

// ===== Эмоции (DUSHA: neutral / angry / positive / sad / other) =====

export const EMOTION_COLORS: Record<string, string> = {
  neutral: '#94a3b8',
  positive: '#22c55e',
  sad: '#3b82f6',
  angry: '#ef4444',
  other: '#eab308',
};

export const EMOTION_ORDER = ['positive', 'neutral', 'sad', 'angry', 'other'];

export function emotionColor(emotion: string): string {
  return EMOTION_COLORS[emotion] || EMOTION_COLORS.other;
}

// ===== Доминирование/динамика =====

export type BalanceTrend = 'dominant-from-start' | 'dominant-grew' | 'balanced-over-time' | 'steady';

/** Простая эвристика «продавил с первой минуты» vs «дискуссия выровнялась»: сравнивает долю
 *  самого доминантного спикера в первом и последнем 5-минутном бакете. */
export function describeBalanceTrend(
  buckets: BalanceDynamicsBucket[],
  mostDominantLabel: string | null,
): BalanceTrend | null {
  if (!buckets.length || !mostDominantLabel) return null;
  const first = buckets[0].by_speaker_percent[mostDominantLabel] ?? 0;
  const last = buckets[buckets.length - 1].by_speaker_percent[mostDominantLabel] ?? 0;
  if (buckets.length === 1) return first > 55 ? 'dominant-from-start' : 'steady';
  if (first > 55 && last > 45) return 'dominant-from-start';
  if (first > 55 && last <= 45) return 'balanced-over-time';
  if (first <= 45 && last > 55) return 'dominant-grew';
  return 'steady';
}

// ===== Форматирование =====

export function roundMinutes(sec: number): number {
  return Math.max(0, Math.round(sec / 60));
}

export function formatPct(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

export function formatSigned(value: number, digits = 1): string {
  const rounded = Number(value.toFixed(digits));
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}
